import React from 'react';

// "Ledger & Seal" family, Dynamic Pricing edition -- shares the shell used
// by RFPResponseOnePager and AgenticReadinessOnePager. No title/video here
// -- heroBanner: true handles that (see useCases.js).
const PALETTE = {
  blueLight: '#60a5fa',
  blue: '#1d4ed8',
  indigo: '#4f46e5',
  violet: '#6d28d9',
  pink: '#9d174d',
};

// Ends on violet, not dark pink like RFP/Agentic.
const stampGradient = `linear-gradient(120deg, ${PALETTE.blueLight} 0%, ${PALETTE.blue} 40%, ${PALETTE.indigo} 70%, ${PALETTE.violet} 100%)`;

// Separate gradient just for the ledger numbers, kept distinct from PALETTE.
const LEDGER_COLORS = ['#60a5fa', '#2563eb', '#4338ca', '#7c3aed', '#9333ea'];

const checklist = [
  { title: 'Ingests sales data on schedule', body: 'Pulls weekly POS data from the ERP automatically. No manual prep required.' },
  { title: 'Researches competitor pricing live, falls back when blocked', body: 'Browser agents navigate retailer sites in real time, extracting prices and capturing screenshots, while AI web search covers additional channels. If a retailer blocks automated access, the system switches to AI web research automatically so the workflow keeps moving.' },
  { title: 'Forecasts revenue impact', body: 'Uses price elasticity calculated from historical data to model 8-week scenarios: Do Nothing vs. Act Now.' },
  { title: 'Routes for human review', body: 'After the report is generated, a reviewer approves or rejects with a written rationale. Decisions are tracked automatically.' },
  { title: 'Structured decision framework', body: 'Four recommendation paths: price increase, reduction, targeted promotion, or hold. Each selected by rule-based logic tied to performance flags and competitive position.' },
];

const stamps = [
  { num: '01', title: 'Connect your data', body: 'ERP exports, POS data, pricing databases' },
  { num: '02', title: 'Configure the workflow', body: 'Multi-step, no code, runs on schedule' },
  { num: '03', title: 'Agents do the analysis', body: 'Performance flags, competitor pricing, forecasts, Excel report' },
  { num: '04', title: 'Your team makes the call', body: 'Review the evidence, approve or reject, rationale required' },
];

const ledgerRows = [
  { label: 'From data ingestion to approved Excel report', value: '7 steps' },
  { label: 'Increase, reduce, promote, or hold', value: '4 paths' },
  { label: 'Forecast, Do Nothing vs. Act Now, traced to elasticity', value: '8-weeks' },
  { label: 'Human review on every recommendation', value: '100%' },
];

const DynamicPricingOnePager = () => {
  return (
    <div className="w-full rounded-2xl bg-[#faf6ee] p-4 sm:p-6">
      <div
        className="relative mb-8 overflow-hidden rounded-md border border-[#e7dcc8] bg-[#fffdf8] p-6 pl-7 shadow-sm"
        style={{ borderLeft: `5px solid ${PALETTE.blueLight}` }}
      >
        <div className="mb-4 text-xs font-bold uppercase tracking-widest" style={{ color: PALETTE.indigo }}>
          Business Case Summary
        </div>

        <div className="mb-4">
          <h4 className="text-xl text-gray-900">The Challenge</h4>
          <p className="mt-1 text-base italic text-gray-500">Reactive pricing decisions that arrive too late to matter.</p>
          <p className="mt-2 text-base leading-relaxed text-gray-700">
            Pricing cycles run on spreadsheets, lag, and guesswork. A competitor drops their price on Monday. Your team finds out Thursday in a weekly report. Someone opens a spreadsheet, manually checks four retail sites, estimates the margin impact, and emails a recommendation up the chain. By the time it is approved, the promotional window has closed and the revenue is gone.
          </p>
        </div>

        <hr className="my-4 border-dashed border-[#e7dcc8]" />

        <div>
          <h4 className="text-xl text-gray-900">The Outcome</h4>
          <p className="mt-1 text-base italic text-gray-500">One workflow replaces five manual handoffs.</p>
          <p className="mt-2 text-base leading-relaxed text-gray-700">
            A scheduled workflow that does the legwork before your team starts their day. An AI-powered automation ingests POS data from the ERP, compares recent sales against 24 months of baselines, researches competitor pricing across retail channels, and calculates price elasticity from observed data. It then forecasts the 8-week revenue impact and routes a recommendation through a structured approval step with full audit trail.
          </p>
        </div>
      </div>

      <h3 className="mb-4 border-b-2 border-gray-900 pb-2 text-2xl text-gray-900">What the automation does</h3>
      <div className="mb-8 overflow-hidden rounded-md border border-[#e7dcc8] bg-[#fffdf8]">
        {checklist.map((item, i) => (
          <div
            key={item.title}
            className={`flex items-start gap-4 px-5 py-4 ${i !== checklist.length - 1 ? 'border-b border-[#f1e9d8]' : ''}`}
          >
            <svg
              className="mt-1.5 h-5 w-5 shrink-0"
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke={PALETTE.blue}
              strokeWidth="3"
              aria-hidden="true"
            >
              <path d="M20 6 9 17l-5-5" />
            </svg>
            <div>
              <h4 className="text-lg font-semibold text-gray-900">{item.title}</h4>
              <p className="mt-1 text-base text-gray-500">{item.body}</p>
            </div>
          </div>
        ))}
      </div>

      <h3 className="mb-4 border-b-2 border-gray-900 pb-2 text-2xl text-gray-900">How it works</h3>
      <div className="relative mb-8 rounded-md border border-[#e7dcc8] bg-[#fffdf8] p-7">
        <div
          className="absolute left-[6%] right-[6%] top-[59px] hidden h-[2px] sm:block"
          style={{ background: stampGradient, opacity: 0.35 }}
          aria-hidden="true"
        />
        <div className="relative grid grid-cols-1 gap-6 sm:grid-cols-4">
          {stamps.map((stamp, i) => (
            <div key={stamp.num} className="text-center">
              <div
                className="mx-auto mb-3 flex h-16 w-16 items-center justify-center rounded-full text-xl font-extrabold text-white"
                style={{
                  background: `linear-gradient(135deg, ${
                    [PALETTE.blueLight, PALETTE.blue, PALETTE.indigo, PALETTE.violet][i]
                  }, ${[PALETTE.blue, PALETTE.indigo, PALETTE.violet, PALETTE.violet][i]})`,
                }}
              >
                {stamp.num}
              </div>
              <h4 className="text-lg font-semibold text-gray-900">{stamp.title}</h4>
              <p className="mt-1 text-base text-gray-500">{stamp.body}</p>
            </div>
          ))}
        </div>
        <p className="relative mt-6 border-t border-b-0 border-l-0 border-r-0 border-dashed border-[#e7dcc8] pt-4 text-base italic text-gray-500">
          Built with browser automation, AI agents, custom Python, exception handling, case management, human-in-the-loop review, and enterprise connectors.
        </p>
      </div>

      <h3 className="mb-4 border-b-2 border-gray-900 pb-2 text-2xl text-gray-900">
        Business Value Created
      </h3>
      <div className="grid grid-cols-1 rounded-md border border-[#e7dcc8] bg-[#fffdf8] sm:grid-cols-4">
        {ledgerRows.map((row, i) => (
          <div
            key={row.label}
            className={`flex flex-col items-center gap-1 px-4 py-5 text-center ${
              i !== ledgerRows.length - 1 ? 'border-b border-[#f1e9d8] sm:border-b-0 sm:border-r' : ''
            }`}
          >
            <span
              className="text-4xl font-extrabold"
              style={{
                background: `linear-gradient(135deg, ${LEDGER_COLORS[i]}, ${LEDGER_COLORS[i + 1]})`,
                WebkitBackgroundClip: 'text',
                backgroundClip: 'text',
                color: 'transparent',
              }}
            >
              {row.value}
            </span>
            <span className="text-base font-semibold text-gray-600">{row.label}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

export default DynamicPricingOnePager;
